import { faHexagonExclamation } from '@fortawesome/pro-solid-svg-icons/faHexagonExclamation';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import clsx from 'clsx';
import { type FC, type PropsWithChildren, useMemo } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeSvgIcon } from 'react-fontawesome-svg-icon';
import { Trans, useTranslation } from 'react-i18next';

import { AuthState, IgnoredError, ReportError, useAuth, useQuery } from '@furality/frontend-stack';
import { Button } from '@furality/furality-purpose';
import fox from '#/api/fox.ts';
import registration from '#/api/registration.ts';
import useAgeVerification from '#/vite/hooks/useAgeVerification.ts';
import Charity from '@/components/_profile/Charity.tsx';
import PaymentHistory from '@/components/_profile/PaymentHistory.tsx';
import Rewards from '@/components/_profile/Rewards.tsx';
import UserCard from '@/components/_profile/UserCard.tsx';
import LinkButton from '@/components/LinkButton.tsx';
import { FullLayout } from '@/components/layouts/index.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';
import PageDisabled from '@/components/PageDisabled.tsx';
import { useModalToggler } from '@/lib/helpers/hash.ts';
import useFlag from '@/lib/hooks/useFlag.ts';
import QUERY_KEYS from '@/lib/QUERY_KEYS.ts';
import useProfileQuery from '@/lib/queries/useProfileQuery.ts';
import useRegistrationQuery from '@/lib/queries/useRegistrationQuery.ts';

const ErrorCard: FC<PropsWithChildren> = ({ children }) => (
  <Card className="bg-fox-gradient-orange hover-shadow-lg p-md-4 px-3 py-4 text-white">
    <Row className="align-items-center">
      <Col md={2} className="d-flex align-items-top justify-content-center mb-md-0 mb-3">
        <FontAwesomeSvgIcon icon={faHexagonExclamation} size="4x" className="text-white" />
      </Col>
      <Col className="text-md-left text-center">{children}</Col>
    </Row>
  </Card>
);

// biome-ignore lint/style/useComponentExportOnlyModules: tanstack route
const Profile = () => {
  const {
    t,
    i18n: { exists: i18nKeyExists },
  } = useTranslation(['common', 'profile', 'status']);

  const auth = useAuth();
  const profile = useProfileQuery();
  const reg = useRegistrationQuery();
  const { isOver18, verificationStatus } = useAgeVerification();

  // fade in page body only if we were loading
  const fadeIn = useMemo(() => auth.state === AuthState.Loading || profile.isLoading, []);

  const [, setShowVoucherModal] = useModalToggler('vouchers');

  const featuresReady = useFlagsStatus().flagsReady;
  const enableCharity = useFlag('charity');
  const enableProfile = useFlag('profile');
  const enableStatusPage = useFlag('status');
  const enableRegistration = useFlag('registration');

  const tier = reg.data?.registrationLevel;
  // const { tier } = useIsRegistered();

  const status = useQuery({
    queryKey: [QUERY_KEYS.Status],
    queryFn: async () => {
      const response = await fox.value.userGetStatus();
      switch (response.status) {
        case 200:
          return response.data;

        case 404:
          throw new IgnoredError(response);
        default:
          throw new ReportError(response);
      }
    },

    enabled: enableStatusPage,
  });

  const charityTotal = useQuery({
    queryKey: ['registration', 'charity'],
    queryFn: async () => {
      const res = await registration.value.paymentGetCharity({ params: { format: '' } });
      switch (res.status) {
        case 200:
          return res.data;
        default:
          throw new ReportError(res);
      }
    },
  });

  // We could just display it as is, but I like the funny count up animation, so we reparse it
  const parsedCharityTotal = parseFloat(
    charityTotal.data?.total.toString().replace(/[^0-9.-]+/g, '') ?? '0',
  );

  if (profile.isError) {
    return <section className="slice">{t('profile:error_loading_profile')}</section>;
  }

  if (!featuresReady || profile.isLoading || profile.isPending) {
    return <PageLoading />;
  }

  if (!enableProfile) {
    return <PageDisabled />;
  }

  return (
    <div className={clsx('mt-4 mt-xl-6 mb-md-4', fadeIn && 'animate__animated animate__fadeIn')}>
      {i18nKeyExists('profile.title') && <h1 className="text-white">{t('profile.title')}</h1>}
      <UserCard profile={profile.data} regLevel={reg.data?.registrationLevel} className="mb-2" />
      {enableStatusPage && status.isSuccess && !status.data.allGood && (
        <ErrorCard>
          <Trans
            ns="profile"
            i18nKey="issues_detected"
            components={{
              h: <h3 className="mt-2" />,
              p: <p className="lead" />,
              Button: (
                <Link to="/status" className="btn btn-outline-white rounded col-md-auto col-12" />
              ),
            }}
          />
        </ErrorCard>
      )}
      {verificationStatus.isFetched && !isOver18 && (
        <ErrorCard>
          <Trans
            ns="status"
            i18nKey="age_gated_instances_not_joinable"
            components={{
              h: <h3 className="my-0" />,
              p: <div className="lead" />,
              Button: (
                <Button
                  variant="outline-white"
                  className="col-md-auto col-12 mt-2"
                  rounded
                  onClick={() => verificationStatus.refetch()}
                />
              ),
              AgeVerificationButton: (
                <Link
                  to="/age-verification"
                  className="btn btn-white rounded col-md-auto col-12 mt-2"
                />
              ),
            }}
          />
        </ErrorCard>
      )}
      {enableRegistration && (
        <div className="flex-center gap-2 mb-4">
          {tier !== 'ultra' && (
            <LinkButton to="/upgrade" className="font-italic ms-2 mb-0" rounded size="sm">
              {t('profile:rewards.upgrade')}
            </LinkButton>
          )}
          <LinkButton
            to="/buy-voucher"
            onClick={() => setShowVoucherModal(true)}
            className="font-italic ms-2 mb-0"
            rounded
            size="sm"
          >
            {t('profile:rewards.buy_vouchers')}
          </LinkButton>
        </div>
      )}

      {enableCharity && (
        <Charity
          charityTotal={parsedCharityTotal}
          showCharityTotal={charityTotal.isSuccess}
          className="mb-5"
        />
      )}
      <Rewards profile={profile.data} className="mb-5" />

      <PaymentHistory />
    </div>
  );
};

export const Route = createFileRoute('/profile')({
  component: () => (
    <FullLayout title="shared.navigation.profile">
      <Profile />
    </FullLayout>
  ),
});
